import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, Clock, Star, Package, Heart,
  MessageCircle, Shield, Search, Truck, CreditCard, Tag,
  ChevronRight, Loader2, X, Lock, Facebook, Instagram, Linkedin
} from 'lucide-react';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import { getTemplateById } from '../config/partnerTemplates';
import { trackWaConversion } from '../utils/trackConversion';
import { injectRochaJsonLd, updateSEO } from '../utils/seoUtils';
import useMetaPixel from '../hooks/useMetaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Benefits Bar ─── */
const BenefitsBar = () => (
  <div className="w-full bg-[#162289] text-white" data-testid="benefits-bar">
    <div className="max-w-[1400px] mx-auto px-4 py-3">
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 text-sm">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-sky-300 flex-shrink-0" />
          <span>Frete calculado por CEP</span>
        </div>
        <div className="hidden sm:block w-px h-5 bg-white/30" />
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-sky-300 flex-shrink-0" />
          <span>Parcele em até 10x no cartão (juros da operadora)</span>
        </div>
        <div className="hidden sm:block w-px h-5 bg-white/30" />
        <div className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-sky-300 flex-shrink-0" />
          <span>Cupom de desconto para parceiros</span>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Partner Footer ─── */
const PartnerFooter = ({ partner }) => (
  <footer className="bg-white border-t border-gray-200" data-testid="partner-footer">
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {/* Central de Atendimento */}
        <div>
          <h4 className="text-base font-bold text-gray-800 mb-4">Central de atendimento</h4>
          <div className="space-y-3 text-sm text-gray-600">
            <a href="tel:6333224310" className="flex items-center gap-2 hover:text-[#162289] transition-colors">
              <Phone className="w-4 h-4 text-[#162289]" />
              <span>(63) 3322-4310</span>
            </a>
            <a href={`https://wa.me/556333224310?text=${encodeURIComponent('🟢 Olá! Vim do site, tenho interesse em comprar peças.')}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-green-600 transition-colors">
              <MessageCircle className="w-4 h-4 text-green-600" />
              <span>(63) 3322-4310</span>
            </a>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#162289]" />
              <span>rochaclimatiza@gmail.com</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#162289]" />
              <span>Seg a Sex 8h-12h / 14h-18h<br/>Sáb 8h-12h</span>
            </div>
          </div>
        </div>

        {/* Menu Rodapé */}
        <div>
          <h4 className="text-base font-bold text-gray-800 mb-4">Menu</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li><a href="#sobre" className="hover:text-[#162289] transition-colors">Sobre nós</a></li>
            <li><a href="#trocas" className="hover:text-[#162289] transition-colors">Trocas e devoluções</a></li>
            <li><a href="#privacidade" className="hover:text-[#162289] transition-colors">Política de privacidade</a></li>
            <li><a href="#contato" className="hover:text-[#162289] transition-colors">Contato</a></li>
          </ul>
        </div>

        {/* Pagamento */}
        <div>
          <h4 className="text-base font-bold text-gray-800 mb-4">Pagamento</h4>
          <div className="flex flex-wrap gap-2 mb-4">
            {['Visa', 'Master', 'Amex', 'Elo', 'Pix'].map(m => (
              <span key={m} className="bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded border border-gray-200">
                {m}
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-500">Boleto bancário disponível</p>

          <h4 className="text-base font-bold text-gray-800 mt-6 mb-3">Segurança</h4>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded border border-gray-200">
              <Lock className="w-3.5 h-3.5 text-green-600" />
              <span className="text-xs font-semibold text-gray-700">SSL 256 bits</span>
            </div>
            <div className="flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded border border-gray-200">
              <Shield className="w-3.5 h-3.5 text-green-600" />
              <span className="text-xs font-semibold text-gray-700">Site Seguro</span>
            </div>
          </div>
        </div>

        {/* Redes Sociais */}
        <div>
          <h4 className="text-base font-bold text-gray-800 mb-4">Redes sociais</h4>
          <div className="flex gap-3 mb-6">
            <a href="https://www.facebook.com/rochaclimatizacao16/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#162289] text-white rounded-full flex items-center justify-center hover:bg-[#1a2fa0] transition-colors" data-testid="social-facebook">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="https://www.instagram.com/rochaclimatizacaopmw/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#162289] text-white rounded-full flex items-center justify-center hover:bg-[#1a2fa0] transition-colors" data-testid="social-instagram">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="#" className="w-10 h-10 bg-[#162289] text-white rounded-full flex items-center justify-center hover:bg-[#1a2fa0] transition-colors" data-testid="social-linkedin">
              <Linkedin className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom bar */}
    <div className="border-t border-gray-200 bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-400">
        <span>&copy; {new Date().getFullYear()} {partner?.name}. Todos os direitos reservados.</span>
        <a href="/brasil" className="text-[#162289]/60 hover:text-[#162289] transition-colors font-medium" data-testid="aug-footer-link">
          Powered by AUG
        </a>
      </div>
    </div>
  </footer>
);

/* ─── Main Page ─── */
const PartnerSitePage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { addToCart } = useCart();
  useMetaPixel(slug);

  const [partner, setPartner] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [showContact, setShowContact] = useState(false);
  const [template, setTemplate] = useState(null);

  const templateParam = searchParams.get('template');

  // SEO + JSON-LD para Rocha
  useEffect(() => {
    if (partner && slug === 'rocha') {
      updateSEO({
        title: 'Rocha Climatização | Peças de Ar Condicionado em Palmas-TO',
        description: 'Compressores, tubos de cobre, gás refrigerante e peças para ar condicionado em Palmas-TO. Frete para todo o Brasil. Parcele em até 10x.',
        ogImage: '/rocha_yinyang.png',
        ogUrl: '/parceiros/rocha',
        ogType: 'website',
      });
      injectRochaJsonLd();
    } else if (partner) {
      document.title = `${partner.name} - Peças para ar condicionado`;
    }
    return () => { document.title = 'Agro Urban Global | Soluções Inteligentes para seu Negócio'; };
  }, [partner, slug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnerRes, productsRes] = await Promise.all([
          fetch(`${API_URL}/api/partners/${slug}`),
          fetch(`${API_URL}/api/partners/${slug}/products?limit=1000`)
        ]);
        if (partnerRes.ok) {
          const partnerData = await partnerRes.json();
          setPartner(partnerData);
          const templateId = templateParam || partnerData.template || 'classic';
          setTemplate(getTemplateById(templateId));
        }
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          setProducts(productsData.products || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, templateParam]);

  const filteredProducts = products.filter(product => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      product.name?.toLowerCase().includes(q) ||
      product.sku?.toLowerCase().includes(q) ||
      product.description?.toLowerCase().includes(q)
    );
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price_asc': return (a.regular_price || 0) - (b.regular_price || 0);
      case 'price_desc': return (b.regular_price || 0) - (a.regular_price || 0);
      case 'rating': return (b.average_rating || 0) - (a.average_rating || 0);
      default: return (a.name || '').localeCompare(b.name || '');
    }
  });

  const handleWhatsApp = () => {
    trackWaConversion();
    window.open('https://wa.me/556333224310?text=' + encodeURIComponent('🟢 Olá! Vim do site, tenho interesse em comprar peças.'), '_blank');
  };

  const handleProductClick = (productId) => {
    navigate(`/parceiros/${slug}/produto/${productId}`);
  };

  const handleToggleFavorite = (e, product) => {
    e.stopPropagation();
    toggleFavorite(product);
  };

  if (loading || !template) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#162289]" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center flex-col gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Parceiro não encontrado</h1>
        <button onClick={() => navigate('/br/products')} className="text-[#162289] hover:underline">
          Voltar aos produtos
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* ═══ HEADER: Loja como marca d'água + logo de perfil + botão ═══ */}
      <div className="w-full bg-[#0a1555]" data-testid="partner-banner">
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <div className="relative overflow-hidden rounded-2xl" style={{ minHeight: '220px' }}>
            {/* Background watermark image - contained within content area */}
            <img
              src="/rocha_banner_header.png"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: 'center center' }}
            />
            <div className="absolute inset-0 bg-[#0a1555]/50" />

            {/* Foreground content: left-aligned logo+name, right-aligned button */}
            <div className="relative z-10 px-8 flex items-center justify-between h-full" style={{ minHeight: '220px' }}>
              {/* Left side: logo + name + tagline */}
              <div className="flex items-center gap-5">
                <div className="w-24 h-24 bg-white rounded-2xl p-1.5 shadow-2xl flex-shrink-0" data-testid="partner-logo">
                  {partner.slug === 'rocha' ? (
                    <img src="/rocha_yinyang.png" alt="Rocha Climatização" className="w-full h-full object-contain rounded-xl" />
                  ) : partner.logo_url ? (
                    <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-contain rounded-xl" />
                  ) : (
                    <div className="w-full h-full bg-gray-100 rounded-xl flex items-center justify-center">
                      <Package className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold text-white drop-shadow-lg">
                    {partner.name}
                  </h1>
                  <p className="text-base text-white/80 mt-1 drop-shadow">Peças para ar condicionado.</p>
                </div>
              </div>

              {/* Right side: button */}
              <button
                onClick={() => setShowContact(true)}
                className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-bold rounded-xl hover:bg-green-600 transition-colors shadow-lg text-sm flex-shrink-0"
                data-testid="header-contact-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Fale Conosco
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ BENEFITS BAR ═══ */}
      <BenefitsBar />

      {/* ═══ SERVICE BUTTON + SEARCH BAR ═══ */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          <button
            onClick={() => setShowContact(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#162289] text-white font-semibold rounded-lg hover:bg-[#1a2fa0] transition-colors text-sm"
            data-testid="services-btn"
          >
            <Phone className="w-4 h-4" />
            Solicitar Serviço / Contato
          </button>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Busque por nome, código ou marca..."
                className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#162289]/30 focus:border-[#162289]"
                data-testid="search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2.5 border border-gray-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#162289]/30"
              data-testid="sort-select"
            >
              <option value="name">Nome (A-Z)</option>
              <option value="price_asc">Menor Preço</option>
              <option value="price_desc">Maior Preço</option>
              <option value="rating">Mais Avaliados</option>
            </select>
          </div>
        </div>
      </div>

      {/* ═══ PRODUCTS SECTION ═══ */}
      <div className="flex-1">
        <div className="max-w-[1400px] mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Nossos Produtos</h2>
              <p className="text-sm text-gray-500">{filteredProducts.length} produtos encontrados</p>
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="products-grid">
            {sortedProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => handleProductClick(product.id)}
                className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                data-testid={`product-card-${product.id}`}
              >
                {/* Image */}
                <div className="relative h-44 overflow-hidden bg-gray-50">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <Package className="w-12 h-12" />
                    </div>
                  )}
                  <button
                    onClick={(e) => handleToggleFavorite(e, product)}
                    className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`fav-btn-${product.id}`}
                  >
                    <Heart className={`w-4 h-4 ${isFavorite(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                  </button>
                  {!product.in_stock && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="bg-white text-gray-800 px-3 py-1 rounded-lg text-sm font-medium">Esgotado</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-3">
                  <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2 min-h-[2.5rem]">
                    {product.name}
                  </h3>
                  <div className="flex items-center gap-1 mb-2">
                    <Star className="w-3 h-3 fill-current text-yellow-400" />
                    <span className="text-xs text-gray-600">{product.average_rating || 0}</span>
                  </div>
                  <div>
                    {product.regular_price ? (
                      <>
                        <p className="text-lg font-bold text-[#162289]">
                          R$ {product.regular_price.toFixed(2)}
                        </p>
                        {product.regular_price > 0 && (
                          <p className="text-xs text-gray-500">
                            até 10x de R$ {(product.regular_price / 10).toFixed(2)}
                          </p>
                        )}
                      </>
                    ) : (
                      <a href={`https://wa.me/556333224310?text=${encodeURIComponent('🟢 Olá! Vim do site, tenho interesse em comprar peças. Produto: ' + product.name)}`}
                        onClick={trackWaConversion}
                        target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-green-600 hover:text-green-700">
                        <MessageCircle className="w-3.5 h-3.5" />
                        Cotar via WhatsApp
                      </a>
                    )}
                  </div>
                  {product.in_stock && (
                    <span className="inline-block mt-1.5 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded font-medium">
                      Em estoque
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {sortedProducts.length === 0 && (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum produto encontrado</h3>
              <p className="text-gray-500">Tente buscar com outros termos.</p>
            </div>
          )}
        </div>
      </div>

      {/* ═══ CONTACT MODAL ═══ */}
      {showContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="contact-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Contato & Serviços</h2>
              <button onClick={() => setShowContact(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-contact-modal">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-3">
              <a href="tel:6333224310"
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="w-11 h-11 bg-blue-100 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#162289]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Telefone</p>
                  <p className="font-semibold text-gray-900">(63) 3322-4310</p>
                </div>
              </a>
              <button onClick={handleWhatsApp}
                className="w-full flex items-center gap-4 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
                <div className="w-11 h-11 bg-green-100 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-left">
                  <p className="text-xs text-gray-500">WhatsApp</p>
                  <p className="font-semibold text-gray-900">(63) 3322-4310</p>
                </div>
              </button>
              <a href="mailto:rochaclimatiza@gmail.com"
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="w-11 h-11 bg-purple-100 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">E-mail</p>
                  <p className="font-semibold text-gray-900">rochaclimatiza@gmail.com</p>
                </div>
              </a>
              {partner?.address && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-11 h-11 bg-orange-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Endereço</p>
                    <p className="font-semibold text-gray-900">{partner.address}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-11 h-11 bg-teal-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Horário de Atendimento</p>
                  <p className="font-semibold text-gray-900 text-sm">Seg a Sex 8h-12h / 14h-18h | Sáb 8h-12h</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PROFESSIONAL FOOTER ═══ */}
      <PartnerFooter partner={partner} />
    </div>
  );
};

export default PartnerSitePage;
